import React from 'react';
import { X, TrendingUp, DollarSign, Send, Eye, MousePointer, AlertTriangle, Users, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

// Stat Card Component
function StatCard({ icon: Icon, label, value, subValue, trend, trendDirection, color = 'blue' }) {
  const colorClasses = {
    blue: 'bg-blue-500/20 text-blue-400',
    green: 'bg-green-500/20 text-green-400',
    gold: 'bg-amber-500/20 text-wt-gold',
    purple: 'bg-purple-500/20 text-purple-400',
    red: 'bg-red-500/20 text-red-400',
  };

  return (
    <div className="p-4 bg-wt-paper rounded-lg border border-wt-border">
      <div className="flex items-start justify-between mb-2">
        <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
          <Icon className="h-4 w-4" />
        </div>
        {trend !== undefined && (
          <div className={`flex items-center text-xs ${trendDirection === 'up' ? 'text-green-400' : 'text-red-400'}`}>
            {trendDirection === 'up' ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
            {trend}%
          </div>
        )}
      </div>
      <p className="text-2xl font-bold text-wt-text-primary">{value}</p>
      <p className="text-xs text-wt-text-muted">{label}</p>
      {subValue && (
        <p className="text-xs text-wt-text-muted mt-1">{subValue}</p>
      )}
    </div>
  );
}

// Node Analytics Component
function NodeAnalytics({ node, stats }) {
  if (!stats) return null;
  
  const nodeStats = stats.node_stats?.[node.id];
  if (!nodeStats) return null;

  return (
    <div className="mt-3 pt-3 border-t border-wt-border">
      <div className="grid grid-cols-4 gap-2 text-center">
        <div>
          <p className="text-sm font-medium text-wt-text-primary">{nodeStats.sent || 0}</p>
          <p className="text-xs text-wt-text-muted">Sent</p>
        </div>
        <div>
          <p className="text-sm font-medium text-wt-text-primary">{nodeStats.delivered || 0}</p>
          <p className="text-xs text-wt-text-muted">Delivered</p>
        </div>
        <div>
          <p className="text-sm font-medium text-wt-text-primary">{nodeStats.read || 0}</p>
          <p className="text-xs text-wt-text-muted">Read</p>
        </div>
        <div>
          <p className="text-sm font-medium text-wt-gold">AED {nodeStats.revenue || 0}</p>
          <p className="text-xs text-wt-text-muted">Revenue</p>
        </div>
      </div>
    </div>
  );
}

export function AnalyticsPanel({ 
  journey, 
  analytics, 
  dateRange, 
  onDateRangeChange, 
  onClose,
  expanded = false 
}) {
  if (!journey) return null;

  // Calculate derived metrics
  const conversionRate = analytics?.conversion_rate || 0;
  const openRate = analytics?.open_rate || 0;
  const clickRate = analytics?.click_rate || 0;
  const totalEntered = analytics?.totals?.entered || journey.total_entered || 0;
  const totalCompleted = analytics?.totals?.completed || journey.total_completed || 0;
  const totalExited = analytics?.totals?.exited || journey.total_exited || 0;
  const totalFailed = analytics?.totals?.failed || 0;
  const revenue = analytics?.revenue || journey.total_revenue || 0;
  
  // WhatsApp message stats
  const whatsappStats = analytics?.whatsapp || {
    sent: 0,
    delivered: 0,
    read: 0,
    failed: 0
  };

  if (!expanded) {
    // Compact sidebar view
    return (
      <div className="w-52 border-r border-wt-border bg-wt-surface p-4 space-y-4 overflow-y-auto">
        <div className="flex items-center justify-between">
          <span className="text-xs text-wt-text-muted font-medium">ANALYTICS</span>
          {onClose && (
            <button onClick={onClose} className="text-wt-text-muted hover:text-wt-text-primary">
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        
        {/* Date Range Selector */}
        <Select value={dateRange} onValueChange={onDateRangeChange}>
          <SelectTrigger className="bg-wt-paper border-wt-border text-wt-text-primary h-8 text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-wt-surface border-wt-border">
            <SelectItem value="7">Last 7 days</SelectItem>
            <SelectItem value="30">Last 30 days</SelectItem>
            <SelectItem value="90">Last 90 days</SelectItem>
            <SelectItem value="all">All time</SelectItem>
          </SelectContent>
        </Select>
        
        {/* Key Metrics */}
        <div className="space-y-3">
          <div className="p-3 bg-wt-paper rounded-lg">
            <p className="text-2xl font-bold text-wt-text-primary">{totalEntered.toLocaleString()}</p>
            <p className="text-xs text-wt-text-muted">Unique Recipients</p>
          </div>
          
          <div className="p-3 bg-wt-paper rounded-lg">
            <p className="text-2xl font-bold text-wt-text-primary">{openRate.toFixed(1)}%</p>
            <p className="text-xs text-wt-text-muted">Open Rate</p>
          </div>
          
          <div className="p-3 bg-wt-paper rounded-lg">
            <p className="text-2xl font-bold text-wt-text-primary">{clickRate.toFixed(1)}%</p>
            <p className="text-xs text-wt-text-muted">Click Rate</p>
          </div>
          
          <div className="p-3 bg-wt-paper rounded-lg">
            <p className="text-2xl font-bold text-wt-text-primary">{conversionRate.toFixed(1)}%</p>
            <p className="text-xs text-wt-text-muted">Conversion Rate</p>
          </div>
          
          <div className="p-3 bg-wt-paper rounded-lg border border-wt-gold/30">
            <p className="text-2xl font-bold text-wt-gold">AED {revenue.toLocaleString()}</p>
            <p className="text-xs text-wt-text-muted">Total Revenue</p>
          </div>
        </div>

        {/* Funnel Stats */}
        <div className="pt-3 border-t border-wt-border">
          <p className="text-xs text-wt-text-muted font-medium mb-2">JOURNEY FUNNEL</p>
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-wt-text-muted">Entered</span>
              <span className="text-wt-text-primary font-medium">{totalEntered}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-wt-text-muted">In Progress</span>
              <span className="text-blue-400 font-medium">{analytics?.totals?.in_progress || 0}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-wt-text-muted">Completed</span>
              <span className="text-green-400 font-medium">{totalCompleted}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-wt-text-muted">Exited Early</span>
              <span className="text-yellow-400 font-medium">{totalExited}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-wt-text-muted">Failed</span>
              <span className="text-red-400 font-medium">{totalFailed}</span>
            </div>
          </div>
        </div>

        {/* Recent Failures - Show if there are failed executions */}
        {analytics?.recent_failures?.length > 0 && (
          <div className="pt-3 border-t border-wt-border">
            <p className="text-xs text-wt-text-muted font-medium mb-2">RECENT FAILURES</p>
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {analytics.recent_failures.map((failure, idx) => (
                <div key={idx} className="p-2 bg-red-500/10 rounded text-xs">
                  <p className="text-red-400 font-medium truncate" title={failure.error}>
                    {failure.error?.substring(0, 50)}{failure.error?.length > 50 ? '...' : ''}
                  </p>
                  <p className="text-wt-text-muted mt-1">
                    {failure.phone || 'No phone'}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* WhatsApp Stats */}
        <div className="pt-3 border-t border-wt-border">
          <p className="text-xs text-wt-text-muted font-medium mb-2">WHATSAPP MESSAGES</p>
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-wt-text-muted">Sent</span>
              <span className="text-wt-text-primary font-medium">{whatsappStats.sent}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-wt-text-muted">Delivered</span>
              <span className="text-blue-400 font-medium">{whatsappStats.delivered}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-wt-text-muted">Read</span>
              <span className="text-green-400 font-medium">{whatsappStats.read}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-wt-text-muted">Failed</span>
              <span className="text-red-400 font-medium">{whatsappStats.failed}</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Full expanded view (for dashboard)
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-wt-text-primary">Journey Analytics</h3>
          <p className="text-sm text-wt-text-muted">{journey.name}</p>
        </div>
        <Select value={dateRange} onValueChange={onDateRangeChange}>
          <SelectTrigger className="bg-wt-paper border-wt-border text-wt-text-primary w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-wt-surface border-wt-border">
            <SelectItem value="7">Last 7 days</SelectItem>
            <SelectItem value="30">Last 30 days</SelectItem>
            <SelectItem value="90">Last 90 days</SelectItem>
            <SelectItem value="all">All time</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Key Metrics Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <StatCard
          icon={Users}
          label="Recipients"
          value={totalEntered.toLocaleString()}
          subValue={`${totalCompleted} completed`}
          color="blue"
        />
        <StatCard
          icon={Send}
          label="Messages Sent"
          value={whatsappStats.sent.toLocaleString()}
          subValue={`${whatsappStats.failed} failed`}
          color="green"
        />
        <StatCard
          icon={Eye}
          label="Open Rate"
          value={`${openRate.toFixed(1)}%`}
          subValue={`${whatsappStats.read} opened`}
          color="purple"
        />
        <StatCard
          icon={MousePointer}
          label="Click Rate"
          value={`${clickRate.toFixed(1)}%`}
          color="blue"
        />
        <StatCard
          icon={DollarSign}
          label="Revenue"
          value={`AED ${revenue.toLocaleString()}`}
          subValue={`${conversionRate.toFixed(1)}% conversion`}
          color="gold"
        />
      </div>

      {/* Conversion Funnel */}
      <div className="p-4 bg-wt-paper rounded-lg border border-wt-border">
        <h4 className="font-medium text-wt-text-primary mb-4">Conversion Funnel</h4>
        <div className="flex items-center justify-between gap-4">
          {[
            { label: 'Entered', value: totalEntered, color: 'bg-blue-500' },
            { label: 'In Progress', value: analytics?.totals?.in_progress || 0, color: 'bg-yellow-500' },
            { label: 'Completed', value: totalCompleted, color: 'bg-green-500' },
            { label: 'Exited', value: totalExited, color: 'bg-orange-500' },
            { label: 'Converted', value: Math.round(totalEntered * conversionRate / 100), color: 'bg-wt-gold' },
          ].map((step, idx) => (
            <div key={idx} className="flex-1 text-center">
              <div className={`h-2 rounded-full ${step.color} mb-2`} style={{
                opacity: 0.3 + (0.7 * step.value / Math.max(totalEntered, 1))
              }} />
              <p className="text-lg font-bold text-wt-text-primary">{step.value}</p>
              <p className="text-xs text-wt-text-muted">{step.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Node Performance */}
      {analytics?.node_stats && Object.keys(analytics.node_stats).length > 0 && (
        <div className="p-4 bg-wt-paper rounded-lg border border-wt-border">
          <h4 className="font-medium text-wt-text-primary mb-4">Node Performance</h4>
          <div className="space-y-3">
            {Object.entries(analytics.node_stats).map(([nodeId, stats]) => (
              <div key={nodeId} className="flex items-center justify-between p-3 bg-wt-bg rounded-lg">
                <div className="flex items-center gap-3">
                  <div className={`w-2 h-2 rounded-full ${
                    stats.type === 'send_whatsapp' ? 'bg-green-500' : 
                    stats.type === 'delay' ? 'bg-blue-500' : 'bg-gray-500'
                  }`} />
                  <span className="text-sm text-wt-text-primary capitalize">
                    {stats.type?.replace(/_/g, ' ')}
                  </span>
                </div>
                <div className="flex items-center gap-6 text-sm">
                  <div className="text-center">
                    <p className="text-wt-text-primary font-medium">{stats.sent || 0}</p>
                    <p className="text-xs text-wt-text-muted">Sent</p>
                  </div>
                  <div className="text-center">
                    <p className="text-wt-text-primary font-medium">{stats.delivered || 0}</p>
                    <p className="text-xs text-wt-text-muted">Delivered</p>
                  </div>
                  <div className="text-center">
                    <p className="text-wt-text-primary font-medium">{stats.read || 0}</p>
                    <p className="text-xs text-wt-text-muted">Read</p>
                  </div>
                  <div className="text-center">
                    <p className="text-wt-gold font-medium">AED {stats.revenue || 0}</p>
                    <p className="text-xs text-wt-text-muted">Revenue</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export { NodeAnalytics };
export default AnalyticsPanel;
